/* eslint-disable max-lines-per-function */
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState, type ReactElement } from "react";
import { Button } from "../../shared/components/ui/button";
import { Label } from "../../shared/components/ui/label";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

type Genre = {
  readonly id: string;
  readonly label: string;
};

type Language = {
  readonly id: string;
  readonly label: string;
};

const GENRES: readonly Genre[] = [
  { id: "drama", label: "Drama" },
  { id: "comedy", label: "Comedy" },
  { id: "thriller", label: "Thriller" },
  { id: "horror", label: "Horror" },
  { id: "romance", label: "Romance" },
  { id: "sci-fi", label: "Sci-Fi" },
  { id: "action", label: "Action" },
  { id: "documentary", label: "Documentary" },
] as const;

const LANGUAGES: readonly Language[] = [
  { id: "es", label: "Spanish" },
  { id: "en", label: "English" },
  { id: "fr", label: "French" },
  { id: "de", label: "German" },
  { id: "it", label: "Italian" },
  { id: "pt", label: "Portuguese" },
  { id: "ja", label: "Japanese" },
  { id: "ko", label: "Korean" },
] as const;

/**
 * Step 2: Content preferences (genres and languages)
 */
export function PreferencesStep(): ReactElement {
  const [selectedGenres, setSelectedGenres] = useState<Set<string>>(new Set());
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(new Set());
  const { transitionTo } = useTransitionNavigation();

  const toggleGenre = (genreId: string): void => {
    setSelectedGenres((prev) => {
      const next = new Set(prev);
      if (next.has(genreId)) {
        next.delete(genreId);
      } else {
        next.add(genreId);
      }
      return next;
    });
  };

  const toggleLanguage = (languageId: string): void => {
    setSelectedLanguages((prev) => {
      const next = new Set(prev);
      if (next.has(languageId)) {
        next.delete(languageId);
      } else {
        next.add(languageId);
      }
      return next;
    });
  };

  const handleContinue = (): void => {
    // Store preferences in localStorage temporarily
    localStorage.setItem("user-genres", JSON.stringify([...selectedGenres]));
    localStorage.setItem("user-languages", JSON.stringify([...selectedLanguages]));
    transitionTo("/user/complete", pageTransitions.slideHorizontal);
  };

  const handleSkip = (): void => {
    localStorage.removeItem("user-genres");
    localStorage.removeItem("user-languages");
    transitionTo("/user/complete", pageTransitions.slideHorizontal);
  };

  return (
    <motion.div
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">What do you want to watch?</h1>
        <p className="text-lg text-neutral-400">Select your favorite genres and languages</p>
      </div>

      <div className="bg-black/50 backdrop-blur-md rounded-lg border border-neutral-800/60 p-8 space-y-8">
        {/* Genres */}
        <div className="space-y-4">
          <Label className="text-base">Favorite Genres</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {GENRES.map((genre) => (
              <button
                key={genre.id}
                type="button"
                onClick={() => toggleGenre(genre.id)}
                className={`px-4 py-3 rounded-lg border transition-all ${
                  selectedGenres.has(genre.id)
                    ? "bg-lumi-accent/20 border-lumi-accent text-white"
                    : "bg-transparent border-neutral-700 text-neutral-400 hover:border-neutral-600 hover:text-white"
                }`}
              >
                {genre.label}
              </button>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div className="space-y-4">
          <Label className="text-base">Preferred Languages</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {LANGUAGES.map((language) => (
              <button
                key={language.id}
                type="button"
                onClick={() => toggleLanguage(language.id)}
                className={`px-4 py-3 rounded-lg border transition-all ${
                  selectedLanguages.has(language.id)
                    ? "bg-lumi-accent/20 border-lumi-accent text-white"
                    : "bg-transparent border-neutral-700 text-neutral-400 hover:border-neutral-600 hover:text-white"
                }`}
              >
                {language.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSkip} variant="outline" className="flex-1">
            Skip
          </Button>
          <Button onClick={handleContinue} className="flex-1 flex items-center justify-center gap-2">
            Continue
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 mt-8">
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
        <div className="w-8 h-1 bg-lumi-accent rounded-full" />
        <div className="w-8 h-1 bg-neutral-800 rounded-full" />
      </div>
      <p className="text-center text-xs text-neutral-500 mt-3">Step 2 of 3</p>
    </motion.div>
  );
}
